import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import { Plus, Search, BookOpen, Instagram, Facebook } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm } from '../../contexts/ConfirmContext';
import { getBlogCategoryLabelKey, BLOG_CATEGORY_VALUES } from '../../i18n/localizeContent';
import { ActionButton, Button, Pagination } from '../../components/UI';

interface SocialMediaPostItem {
  _id: string;
  subject: string;
  description: string;
  category: string;
  status: 'draft' | 'scheduled' | 'publishing' | 'published' | 'failed' | 'partial';
  targets: { blog: boolean; instagram: boolean; facebook: boolean };
  image?: { url?: string };
  author: { name: string };
  scheduledAt?: string;
  publishedAt?: string;
  createdAt: string;
}

const STATUS_VALUES = ['draft', 'scheduled', 'publishing', 'published', 'failed', 'partial'];

const AdminRedesSociais: React.FC = () => {
  const { t } = useTranslation();
  const [posts, setPosts] = useState<SocialMediaPostItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const notifications = useNotifications();
  const confirm = useConfirm();

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10',
        ...(searchTerm && { search: searchTerm }),
        ...(selectedCategory && { category: selectedCategory }),
        ...(selectedStatus && { status: selectedStatus }),
      });

      const response = await axios.get(`/redes-sociais?${params}`);
      setPosts(response.data.posts);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Erro ao carregar posts de redes sociais:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedCategory, selectedStatus, currentPage]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleDelete = async (postId: string) => {
    if (!(await confirm({ message: t('admin.socialPostDeleteConfirm'), confirmText: t('common.delete'), variant: 'danger' }))) return;
    try {
      await axios.delete(`/redes-sociais/${postId}`);
      setPosts(posts.filter((post) => post._id !== postId));
      notifications.success.socialPostDeleted();
    } catch (error: any) {
      console.error('Erro ao deletar post de redes sociais:', error);
      notifications.error.socialPostDelete(error.response?.data?.message || 'Erro ao deletar post');
    }
  };

  const handlePublishNow = async (postId: string) => {
    try {
      setPublishingId(postId);
      const response = await axios.post(`/redes-sociais/${postId}/publish-now`);
      setPosts(posts.map((post) => (post._id === postId ? response.data : post)));
      notifications.success.socialPostPublished();
    } catch (error: any) {
      console.error('Erro ao publicar post de redes sociais:', error);
      notifications.error.socialPostPublish(error.response?.data?.message || 'Erro ao publicar post');
    } finally {
      setPublishingId(null);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      draft: 'bg-gray-100 text-gray-800',
      scheduled: 'bg-blue-100 text-blue-800',
      publishing: 'bg-yellow-100 text-yellow-800',
      published: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      partial: 'bg-orange-100 text-orange-800',
    };
    return colors[status] || colors.draft;
  };

  const getStatusLabel = (status: string) => {
    const labels: { [key: string]: string } = {
      draft: 'Rascunho',
      scheduled: 'Agendado',
      publishing: 'Publicando',
      published: 'Publicado',
      failed: 'Falhou',
      partial: 'Parcial',
    };
    return labels[status] || status;
  };

  // Ações da linha: mesma marcação na tabela (desktop) e nos cards (mobile).
  const renderActions = (post: (typeof posts)[number]) => (
    <div className="row-actions md:justify-end">
      {['draft', 'scheduled', 'failed', 'partial'].includes(post.status) && (
        <ActionButton
          action="send"
          label="Publicar"
          onClick={() => handlePublishNow(post._id)}
          disabled={publishingId === post._id}
        />
      )}

      <ActionButton
        action="edit"
        to={`/admin/redes-sociais/${post._id}/edit`}
      />

      <ActionButton
        action="delete"
        onClick={() => handleDelete(post._id)}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Breadcrumbs />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('admin.socialMedia')}</h1>
              <p className="text-gray-600">{t('admin.socialMediaDesc')}</p>
            </div>
            <Button className="self-start" to="/admin/redes-sociais/new">
              <Plus className="w-5 h-5" />
              <span>{t('admin.socialPostNew')}</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder={t('admin.postsSearchPh')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="field pl-10 pr-4"
                />
              </div>
            </div>

            <div className="lg:w-56">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="field field--select"
              >
                <option value="">{t('blog.all')}</option>
                {BLOG_CATEGORY_VALUES.map((category) => (
                  <option key={category} value={category}>
                    {t(getBlogCategoryLabelKey(category))}
                  </option>
                ))}
              </select>
            </div>

            <div className="lg:w-48">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="field field--select"
              >
                <option value="">Todos os status</option>
                {STATUS_VALUES.map((status) => (
                  <option key={status} value={status}>
                    {getStatusLabel(status)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">{t('common.loading')}</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-gray-400 mb-4">
                <Search className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('admin.socialPostsEmpty')}</h3>
              <p className="text-gray-600 mb-4">Comece criando seu primeiro post de redes sociais.</p>
              <Button to="/admin/redes-sociais/new">
                Criar Primeiro Post
              </Button>
            </div>
          ) : (
            <>
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Post</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Categoria</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Canais</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Autor</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {posts.map((post) => (
                    <tr key={post._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 max-w-xs">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-12 w-12">
                            {post.image?.url ? (
                              <img className="h-12 w-12 rounded-lg object-cover" src={post.image.url} alt={post.subject} />
                            ) : (
                              <div className="h-12 w-12 bg-gray-200 rounded-lg flex items-center justify-center">
                                <span className="text-gray-400 text-xs">Sem imagem</span>
                              </div>
                            )}
                          </div>
                          <div className="ml-4 min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 truncate">{post.subject}</div>
                            <div className="text-sm text-gray-500 truncate">{post.description}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                          {post.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(post.status)}`}>
                          {getStatusLabel(post.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-1.5">
                          <BookOpen className={`w-4 h-4 ${post.targets?.blog ? 'text-blue-600' : 'text-gray-300'}`} />
                          <Instagram className={`w-4 h-4 ${post.targets?.instagram ? 'text-pink-600' : 'text-gray-300'}`} />
                          <Facebook className={`w-4 h-4 ${post.targets?.facebook ? 'text-blue-800' : 'text-gray-300'}`} />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {post.author?.name || 'Autor não definido'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(post.scheduledAt || post.publishedAt || post.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {renderActions(post)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul className="md:hidden divide-y divide-gray-200">
              {posts.map((post) => (
                <li key={post._id} className="p-4 space-y-3">
                  <div className="min-w-0">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-12 w-12">
                        {post.image?.url ? (
                          <img className="h-12 w-12 rounded-lg object-cover" src={post.image.url} alt={post.subject} />
                        ) : (
                          <div className="h-12 w-12 bg-gray-200 rounded-lg flex items-center justify-center">
                            <span className="text-gray-400 text-xs">Sem imagem</span>
                          </div>
                        )}
                      </div>
                      <div className="ml-4 min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 truncate">{post.subject}</div>
                        <div className="text-sm text-gray-500 truncate">{post.description}</div>
                      </div>
                    </div>
                  </div>
                  <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
                    <div className="min-w-0">
                      <dt className="text-xs text-gray-500">Categoria</dt>
                      <dd className="text-sm text-gray-700 break-words">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                          {post.category}
                        </span>
                      </dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="text-xs text-gray-500">Status</dt>
                      <dd className="text-sm text-gray-700 break-words">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(post.status)}`}>
                          {getStatusLabel(post.status)}
                        </span>
                      </dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="text-xs text-gray-500">Canais</dt>
                      <dd className="text-sm text-gray-700 break-words">
                        <div className="flex items-center space-x-1.5">
                          <BookOpen className={`w-4 h-4 ${post.targets?.blog ? 'text-blue-600' : 'text-gray-300'}`} />
                          <Instagram className={`w-4 h-4 ${post.targets?.instagram ? 'text-pink-600' : 'text-gray-300'}`} />
                          <Facebook className={`w-4 h-4 ${post.targets?.facebook ? 'text-blue-800' : 'text-gray-300'}`} />
                        </div>
                      </dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="text-xs text-gray-500">Autor</dt>
                      <dd className="text-sm text-gray-700 break-words">
                        {post.author?.name || 'Autor não definido'}
                      </dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="text-xs text-gray-500">Data</dt>
                      <dd className="text-sm text-gray-700 break-words">
                        {formatDate(post.scheduledAt || post.publishedAt || post.createdAt)}
                      </dd>
                    </div>
                  </dl>
                  {renderActions(post)}
                </li>
              ))}
            </ul>
            </>
          )}

          <Pagination currentPage={currentPage} totalPages={totalPages} onChange={setCurrentPage} />
        </div>
      </div>
    </div>
  );
};

export default AdminRedesSociais;
